import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { formatDataBr } from '../../hooks/useTarefas';

function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultPeriodo() {
  const ate = new Date();
  const de = new Date();
  de.setDate(de.getDate() - 30);
  return { de: toIsoDate(de), ate: toIsoDate(ate) };
}

export default function TarefasIndicadoresView({ carregarIndicadores }) {
  const [periodo, setPeriodo] = useState(defaultPeriodo);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await carregarIndicadores(periodo.de, periodo.ate);
        if (!cancelled) setDados(r);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [periodo.de, periodo.ate, carregarIndicadores]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const resumo = dados?.resumo || {};

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
        <TextField
          label="De"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={periodo.de}
          onChange={(e) => setPeriodo((p) => ({ ...p, de: e.target.value }))}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={periodo.ate}
          onChange={(e) => setPeriodo((p) => ({ ...p, ate: e.target.value }))}
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          Padrão: últimos 30 dias
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Criadas', valor: resumo.criadas ?? 0 },
          { label: 'Concluídas', valor: resumo.concluidas ?? 0 },
          { label: 'Em aberto', valor: resumo.emAberto ?? 0 },
          {
            label: 'Tempo médio (h)',
            valor: resumo.tempoMedioHoras != null ? resumo.tempoMedioHoras : '—'
          }
        ].map((c) => (
          <Grid item xs={6} sm={3} key={c.label}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.04)' }}>
              <Typography variant="h5" fontWeight={700}>
                {c.valor}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {c.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {(dados?.porDia || []).length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Criadas vs concluídas por dia
          </Typography>
          <Paper sx={{ p: 2, mb: 3, height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dados.porDia}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="dia" tick={{ fontSize: 10 }} tickFormatter={(v) => formatDataBr(v)} />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={(v) => formatDataBr(v)} />
                <Legend />
                <Bar dataKey="criadas" name="Criadas" fill="#8ab4f8" />
                <Bar dataKey="concluidas" name="Concluídas" fill="#81c995" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      {(dados?.porLista || []).length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Por grupo / lista de tarefas
          </Typography>
          <Paper sx={{ p: 2, mb: 3, height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dados.porLista} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="nome" tick={{ fontSize: 11 }} width={75} />
                <Tooltip />
                <Legend />
                <Bar dataKey="criadas" name="Criadas" fill="#8ab4f8" />
                <Bar dataKey="concluidas" name="Concluídas" fill="#81c995" />
                <Bar dataKey="pendentes" name="Pendentes" fill="#ff9800" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Lista</TableCell>
                <TableCell align="right">Criadas</TableCell>
                <TableCell align="right">Concluídas</TableCell>
                <TableCell align="right">Pendentes</TableCell>
                <TableCell align="right">Tempo médio (h)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dados.porLista.map((r) => (
                <TableRow key={r.listaId}>
                  <TableCell>{r.nome}</TableCell>
                  <TableCell align="right">{r.criadas}</TableCell>
                  <TableCell align="right">{r.concluidas}</TableCell>
                  <TableCell align="right">{r.pendentes}</TableCell>
                  <TableCell align="right">{r.tempoMedioHoras ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {(dados?.atencao || []).length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Precisam de atenção (mais tempo em aberto)
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Tarefa</TableCell>
                <TableCell>Lista</TableCell>
                <TableCell align="right">Dias aberta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dados.atencao.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.titulo}</TableCell>
                  <TableCell>{r.listaNome}</TableCell>
                  <TableCell align="right">{r.diasAberta}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {(dados?.tarefasLentas || []).length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Tarefas que mais demoraram para concluir
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tarefa</TableCell>
                <TableCell>Lista</TableCell>
                <TableCell align="right">Horas</TableCell>
                <TableCell>Finalizada</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dados.tarefasLentas.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.titulo}</TableCell>
                  <TableCell>{r.listaNome}</TableCell>
                  <TableCell align="right">{r.duracaoHoras}</TableCell>
                  <TableCell>{formatDataBr(r.concluidaEm)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}
